import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import type { TextProps, Text as RNText } from 'react-native';

import { TYPE_CONFIG } from '@garnish/constants';
import { useFluidTextStyles } from '../useFluidTextStyles';
import { Text } from '../Text';

type Props = {
  readonly size?: SizeOrdinal;
  readonly wide?: boolean;
  readonly underline?: boolean;
  readonly children?: ReactNode;
} & TextProps;

export const DisplayText = React.forwardRef(
  (props: Props, ref: React.Ref<RNText>): ReactElement<Text> => {
    const {
      size = DEFAULT_SIZE,
      wide = false,
      style,
      children,
      ...rest
    } = props;

    const sizeKey = wide ? PRESET_MAP_WIDE[size] : PRESET_MAP[size];
    const configKey = wide ? 'DISPLAY_WIDE' : 'DISPLAY';

    const responsiveStyles = useFluidTextStyles(sizeKey, configKey);
    const stylesFromPresets = getStylesFromPresets({ size, wide });

    const displayStyles = [stylesFromPresets, responsiveStyles, style];

    return (
      <Text ref={ref} style={displayStyles} {...rest}>
        {children}
      </Text>
    );
  },
);

/* -- utils, etc ------------------------------------------------------------ */

const PRESET_MAP = {
  1: '48',
  2: '40',
  3: '32',
  4: '24',
};

const PRESET_MAP_WIDE = {
  1: '80',
  2: '40',
  3: '32',
  4: '32',
};

const DEFAULT_SIZE: SizeOrdinal = 1;

type GetPresetProps = {
  readonly size: SizeOrdinal;
  readonly wide: boolean;
};

const getStylesFromPresets = ({ size, wide }: GetPresetProps) => {
  const { DISPLAY, DISPLAY_WIDE } = TYPE_CONFIG;

  return wide ? DISPLAY_WIDE[PRESET_MAP_WIDE[size]] : DISPLAY[PRESET_MAP[size]];
};

type SizeOrdinal = keyof typeof PRESET_MAP | keyof typeof PRESET_MAP_WIDE;
